import React from 'react';
import ActionList from '~/lib/ui/actionList';
import { Popover, PopoverContent, PopoverAnchor } from '~/lib/ui/popover';
import { cn } from '~/lib/utils';
import { useCurrentUserContext } from '../../hooks/useCurrentContext';
import { useReactionHandler } from '../../hooks/useReactionHandler';
import { useReactionPopover } from '../../hooks/useReactionPopover';
import { useReactionUserData } from '../../hooks/useReactionUserData';
import { EmojiReactionDisplayProps } from './index.types';

/**
 * EmojiReactionDisplay - Displays existing emoji reactions for a message
 *
 * This component renders a horizontal list of emoji reaction buttons that show:
 * - The emoji icon
 * - The count of users who reacted with that emoji
 * - A popover on hover showing the list of users who reacted
 */
export const EmojiReactionDisplay = ({
  testID,
  reactions,
  messageId,
  ...props
}: EmojiReactionDisplayProps): React.ReactElement | null => {
  const { currentUserId, currentConversationId } = useCurrentUserContext();

  const { hoveredEmoji, handleMouseEnter, handleMouseLeave } =
    useReactionPopover();

  const handleReactionClick = useReactionHandler(
    reactions,
    currentUserId,
    currentConversationId,
    messageId
  );

  const { getUserName } = useReactionUserData(reactions);

  const sortedReactions = React.useMemo(() => {
    if (!reactions) return [];

    return Object.entries(reactions)
      .map(([emoji, userIds]) => ({
        emoji,
        userIds,
        count: userIds.length,
        hasCurrentUser: userIds.includes(currentUserId),
      }))
      .sort((a, b) => b.count - a.count);
  }, [reactions, currentUserId]);

  if (sortedReactions.length === 0) {
    return null;
  }

  const componentTestId = testID
    ? `${testID}-emoji-reaction-display`
    : 'emoji-reaction-display';

  const { className, ...restProps } = props;

  return (
    <div
      className={cn("gap-1 items-center ml-2 relative flex flex-row", className)}
      data-testid={componentTestId}
      role="group"
      aria-label="Message reactions"
      {...restProps}
    >
      {sortedReactions.map(({ emoji, count, hasCurrentUser, userIds }) => {
        const isOpen = hoveredEmoji === emoji;
        const popoverId = `popover-${messageId}-${emoji}`;

        return (
          <Popover key={emoji} open={isOpen}>
            <PopoverAnchor asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center gap-[4px] h-[25px] p-[5px] border rounded-2xl cursor-pointer',
                  'transition-all duration-100 ease-out leading-none min-h-6',
                  'bg-background hover:bg-border',
                  'focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none',
                  'shadow-sm',
                  hasCurrentUser && 'bg-background border-primary'
                )}
                onClick={() => handleReactionClick(emoji)}
                onMouseEnter={() => handleMouseEnter(emoji)}
                onMouseLeave={handleMouseLeave}
                onFocus={() => handleMouseEnter(emoji)}
                onBlur={handleMouseLeave}
                data-testid={`emoji-reaction-display-${emoji}`}
                aria-label={`${emoji} reaction, ${count} ${
                  count === 1 ? 'person' : 'people'
                } reacted${
                  hasCurrentUser
                    ? ', including you. Press to remove your reaction'
                    : '. Press to add your reaction'
                }`}
                aria-pressed={hasCurrentUser}
                aria-describedby={isOpen ? popoverId : undefined}
              >
                <span
                  className="flex-shrink-0 flex items-center justify-center select-none text-base w-[16px] h-[16px]"
                  aria-hidden="true"
                >
                  {emoji}
                </span>
                <span
                  className="font-normal text-sm leading-none"
                  aria-hidden="true"
                >
                  {count}
                </span>
              </button>
            </PopoverAnchor>

            {isOpen && (
              <PopoverContent
                id={popoverId}
                role="tooltip"
                side="bottom"
                arrowOffset={0}
                className="flex flex-col w-[190px] animate-duration-200 p-0"
                onMouseEnter={() => handleMouseEnter(emoji)}
                onMouseLeave={handleMouseLeave}
                aria-label={`People who reacted with ${emoji}`}
              >
                <div className="p-1 max-h-[290px] overflow-y-auto scroll-area-content">
                  {userIds.map((userId) => (
                    <ActionList key={userId}>
                      <ActionList.Item
                        text={getUserName(userId)}
                        hasAvatar
                        isSelected={userId === currentUserId}
                      />
                    </ActionList>
                  ))}
                </div>
              </PopoverContent>
            )}
          </Popover>
        );
      })}
    </div>
  );
};

EmojiReactionDisplay.displayName = 'EmojiReactionDisplay';